import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import {
  fetchProjectActivity,
  type ProjectActivityChannel,
} from "@/stores/organizations/projectActivityRepository";
import {
  portfolioPreferenceKey,
  usePortfolioPreference,
  writePortfolioPreference,
} from "@/components/building/portfolioPreferences";

const ACTIVITY_POLL_MS = 30_000;

function useActivityKey(projectId: number, channel: ProjectActivityChannel) {
  const { userId } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = currentOrganization?.id;
  const key = portfolioPreferenceKey(userId, orgId, String(projectId), `activity-${channel}`);
  return { key, orgId, userId, ready: !!key && isReady && projectId > 0 };
}

/**
 * True when the project's documents or issues changed since this user last
 * opened that page for it. A project never opened shows no dot: there is no
 * "last seen" to be newer than, and a portfolio of first-visit dots says
 * nothing. Polls while the tab is in the foreground.
 */
export function useProjectActivityDot(
  projectId: number,
  channel: ProjectActivityChannel,
  enabled: boolean
): boolean {
  const scope = useActivityKey(projectId, channel);
  const seen = usePortfolioPreference(scope.key);
  const query = useQuery({
    queryKey: ["project-activity", scope.userId, scope.orgId, projectId, channel],
    queryFn: () => fetchProjectActivity(scope.orgId!, projectId, channel),
    enabled: enabled && scope.ready && seen !== null,
    staleTime: ACTIVITY_POLL_MS,
    refetchInterval: ACTIVITY_POLL_MS,
  });
  return enabled && scope.ready && seen !== null && query.isSuccess && query.data.revision !== seen;
}

/**
 * Marks a project's documents or issues as seen when its page has loaded for
 * that project. The snapshot is read then, so a change landing while the page
 * is open is shown by the page's own live updates rather than left as a dot.
 */
export function useMarkProjectActivitySeen(
  projectId: number | null,
  channel: ProjectActivityChannel,
  pageLoaded: boolean
): void {
  const scope = useActivityKey(projectId ?? 0, channel);
  const markedRef = useRef<string | null>(null);
  const identity = scope.ready && projectId != null ? scope.key : null;
  useEffect(() => {
    const key = scope.key;
    if (!pageLoaded || !identity || !key || !scope.orgId || projectId == null) return;
    if (markedRef.current === identity) return;
    markedRef.current = identity;
    // Not cancelled on cleanup: the write is keyed to this project, so it is
    // right whenever it lands, and dropping it would leave the dot up.
    fetchProjectActivity(scope.orgId, projectId, channel)
      .then((snapshot) => writePortfolioPreference(key, snapshot.revision))
      .catch(() => {
        // A failed read never acknowledges anything; the next load retries.
        if (markedRef.current === identity) markedRef.current = null;
      });
  }, [pageLoaded, identity, scope.orgId, scope.key, projectId, channel]);
}
